import React, { memo, useCallback, useMemo } from "react";
import {
  Center,
  Divider,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  IconButton,
  Spacer,
  Text,
  VStack,
} from "@chakra-ui/react";
import colors from "app/colors";

import { useTags } from "modules/providers/tags-provider";
import { HASH_KEY } from "modules/utils/Constants";
import { BulletListIcon, PencilOutlineIcon } from "modules/icons";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";

interface IProps {
  tagCategoryId: string;
}

const AdminTagCategoryDrawer = ({ tagCategoryId }: IProps) => {
  const { openModal } = useOverlay();
  const { tags, tagCategoriesById } = useTags();

  const onEditClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.ADMIN_TAG_CATEGORY,
      id: tagCategoryId,
    });
  }, [openModal, tagCategoryId]);

  const tagCategory = useMemo(
    () => tagCategoriesById[tagCategoryId],
    [tagCategoryId, tagCategoriesById],
  );

  const tagList = useMemo(
    () => tags.filter((obj) => obj.tagCategoryId === tagCategoryId),
    [tagCategoryId, tags],
  );

  if (!tagCategory) {
    return null;
  }

  return (
    <DrawerContent>
      <DrawerCloseButton top={5} left={5} color={colors.gray30} />
      <DrawerHeader>
        <Center fontWeight={700} fontSize={26} color={colors.blackSecondary}>
          Tag Category Details
        </Center>
        <Center height={10}>
          <Divider />
        </Center>
      </DrawerHeader>
      <DrawerBody>
        <VStack w="full" alignItems="flex-start">
          <HStack w="full" spacing={0}>
            <Text fontSize={28} fontWeight={700}>
              {tagCategory.name}
            </Text>
            <Spacer />
            <IconButton
              aria-label="edit tag category"
              variant="ghost"
              onClick={onEditClicked}
              icon={<PencilOutlineIcon boxSize={6} fill={colors.grayPrimary} />}
            />
          </HStack>
          {/* <VStack w="full" spacing={0} alignItems="flex-start">
            <Checkbox
              fontSize={14}
              fontWeight={700}
              color={colors.gray50}
              defaultChecked={tagCategory.type === TagCategoryType.UserTag}
              onChange={onUseAsUserAttributeClicked}
            >
              Use as User Attribute
            </Checkbox>
            <Text fontSize={12} fontWeight={400} color={colors.grayMain} pl={6}>
              Values in this category added to users in admin
            </Text>
          </VStack> */}

          {tagList.length > 0 && (
            <>
              <HStack>
                <BulletListIcon boxSize={6} />
                <Text fontWeight={700}>Values ({tagList.length})</Text>
              </HStack>
              <Divider borderColor={colors.black} />
              <Text fontSize={11} color={colors.grayMain} pb={2}>
                Tags within this category
              </Text>
              {tagList.map((tag) => (
                <React.Fragment key={tag.id}>
                  <Text color={colors.gray50} fontSize={12} fontWeight={700}>
                    {`${HASH_KEY} ${tag.name}`}
                  </Text>
                  <Divider />
                </React.Fragment>
              ))}
            </>
          )}
        </VStack>
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(AdminTagCategoryDrawer);
